import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push as pushState, replace as replaceState } from 'connected-react-router';
import { bindActionCreators } from 'redux';
import * as symbolActions from 'reducers/symbol';
import { SYMBOL_TIPS, IOS_SYMBOL_TOOL, ANDROID_SYMBOL_TOOL } from 'utils/constants';
import _style from './style.scss';
import { withTranslation } from 'react-i18next';
import {
  isAndroidOrHarmony,
  isIos,
  isMobile,
  isPcOrLinux,
  isPlaystation, isSwitch,
  isWechatMiniProgram,
  PlatformUtil,
} from 'utils/platform';
import { EXCEPTION_TYPE_INT_ANDROID_JAVA } from 'utils/constants/exception-type-int';
import isEqual from 'lodash/lang/isEqual';
import { is } from 'immutable';
import { Button, Popconfirm, Space, Table, Tag, Tooltip } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { isNotNullish } from 'utils/nullish';
import { ze } from 'utils/zhEn';
import { CS_STYLES } from 'utils/constants/style-constants';
import { orange, red } from '@ant-design/colors';
import { DataDetailUtil } from 'components/exception/issue/DataDetail';
import { TimeZoneUtil } from 'utils/time-zone-util';
import { getBackendInjectEnvs } from 'utils/backend-inject-envs';
import { BillingTeamType } from 'utils/constants/billing';
import { DocsUtil } from 'utils/docs-util';
import CsDownloadButton from 'components/commons/CsDownloadButton/CsDownloadButton';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';
import IconRight from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_symbolfile_list_right_icon.svg';
import IconWrong from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_symbolfile_list_wrong_icon.svg';
import IconManage from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_symbolfile_managebtn_icon.svg';
import { CrashModuleUtil } from 'utils/crash-module-util';
import { MinidumpSymbolType, MinidumpSymbolTypeUtil } from 'utils/constants/minidump-symbol-type';
import { CrashUtil } from 'utils/api/crash';
import { ServerAppSettings } from 'utils/server-app-settings';
import { CosUtil } from 'utils/cos-util';

@withTranslation()
class SymbolTable extends Component {
  componentDidMount() {
    this.mountAndUpdateCommonHandler(undefined);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.mountAndUpdateCommonHandler(prevProps);
  }

  mountAndUpdateCommonHandler(prevProps) {
    const {
      symbolInfo: prevSymbolInfo,
      issue: prevIssue,
    } = prevProps || {};
    const { symbolInfo, issue, pid } = this.props;

    if (is(prevIssue, issue) && isEqual(symbolInfo, prevSymbolInfo)) {
      return;
    }

    // 检查tip状态
    const { crashDoc, issueExceptionType } = issue.toJS();
    if (!crashDoc || !symbolInfo) {
      return;
    }
    // 发送更新tips的action
    if (this.getShouldRenderNative()) {
      this.getNativeSymbolInfo(symbolInfo, issue, true);
    } else if (this.getShouldRenderJava()) {
      this.getJavaSymbolInfo(symbolInfo, issue, true);
    }
  }

  getServerAppSettings() {
    const { appId, reduxState } = this.props;
    return reduxState.app.get('appIdToServerAppSettings')[appId] || {};
  }

  getIsEnableUsym() {
    const serverAppSettings = this.getServerAppSettings();
    return !!serverAppSettings[ServerAppSettings.keys.enableUsym];
  }

  formatWithAppTimeZone(serverTime) {
    const { currentApp } = this.props;
    const utcOffset = currentApp.get('utcOffset');
    const timeStr = TimeZoneUtil.convertDefaultTimeZoneLocalDateTimeToUtcOffsetMoment(serverTime, utcOffset).format('YYYY-MM-DD HH:mm:ss');
    const offsetStr = TimeZoneUtil.makeUtcOffsetLabel(utcOffset);
    return offsetStr ? `${timeStr} (${offsetStr})` : timeStr;
  }

  getShouldRenderJava() {
    const { issue, pid } = this.props;
    if (isWechatMiniProgram(pid)) {
      return true;
    }
    if (!isAndroidOrHarmony(pid)) {
      return false;
    }

    const { issueExceptionType, crashDoc } = issue.toJS();
    const { retraceCrashDetail } = crashDoc.crashMap;
    const hasJavaStack = /java/i.test(retraceCrashDetail);
    return Number(issueExceptionType) === EXCEPTION_TYPE_INT_ANDROID_JAVA && hasJavaStack;
  }

  getShouldRenderNative() {
    const { issue, pid } = this.props;
    if ((PlatformUtil.isIosOrMac(pid) || isPcOrLinux(pid) || isPlaystation(pid))) {
      return true;
    }
    const { crashDoc } = issue.toJS();
    const { soInfo, reservedMap } = crashDoc?.crashMap || {};
    return !!soInfo || !!reservedMap?.il2cppUUID;
  }

  getJavaSymbolInfo(infos, issue, shouldSendAction) {
    const {
      uploadingId, uploadPercent, currentApp, actions,
    } = this.props;
    const { appId, pid } = currentApp.toJS();
    const { crashDoc } = issue.toJS();
    const channel = crashDoc.crashMap.channelId;
    const version = crashDoc.crashMap.productVersion;

    const thisCrashChannels = infos.filter((info) => info.channel === channel);
    const isThisCrashChannelUpload = thisCrashChannels.length > 0;
    const thisCrashChannelInfo = isThisCrashChannelUpload && thisCrashChannels[0];

    const allChannels = infos.filter((info) => info.channel === '-1');
    const isAllChannelUpload = (allChannels.length > 0);
    const allChannelInfo = isAllChannelUpload && allChannels[0];

    // 更新tips
    if (shouldSendAction) {
      if (!isThisCrashChannelUpload && !isAllChannelUpload) {
        actions.changeSymbolTips('no_symbol');
      } else {
        actions.changeSymbolTips('');
      }
    }

    return {
      uploadingId,
      uploadPercent,
      appId,
      pid,
      channel,
      version,
      isThisCrashChannelUpload,
      thisCrashChannelInfo,
      isAllChannelUpload,
      allChannelInfo,
    };
  }

  /**
   * @param uploadedInfos {{ uuid, soName, arch, uploadTime, productVersion, fileName, filePath }[]}
   */
  getNativeSymbolInfo(uploadedInfos, issue, shouldSendAction) {
    const {
      uploadingId, uploadPercent, actions, currentApp,
    } = this.props;
    const { appId, pid } = currentApp.toJS();
    const {
      attachList,
      crashDoc,
      pcModuleInfoList,
      isShowingMinidumpStack,
      attachAllKvList,
    } = issue.toJS();
    const { productVersion } = crashDoc.crashMap || {};
    const {
      soInfo,
      appUUID,
      systemSoNameList,
    } = crashDoc.crashMap || {};
    const { systemSymbolUuids, appSoNameList } = crashDoc.esMap || {};
    // 这个目前只有AOS/IOS有
    const lowerSystemSoNameList = (systemSoNameList || []).map(x => (x || '').toLowerCase());
    // 这个目前只有MAC有
    const lowerAppSoNameList = (appSoNameList || []).map(x => (x || '').toLowerCase());
    // 这个目前只有PC有
    const lowerSystemSymbolUuids = (systemSymbolUuids || []).map(x => (x || '').toLowerCase());

    const { ignoreSymbolInfo } = this.props;
    if (!ignoreSymbolInfo) {
      return <div></div>;
    }

    const enableUsym = this.getIsEnableUsym();

    let soInfos = [];
    if ((isAndroidOrHarmony(pid) || isSwitch(pid)) && !isShowingMinidumpStack) {
      const uuids = CrashUtil.getSymbolUuidList({ pid, crashDoc, attachList, pcModuleInfoList, attachAllKvList, enableUsym });
      const uuidSoInfoEntries = (soInfo || '').split('|')
        .filter((str) => !!str)
        .map((info) => {
          const tmp = info.split(':');
          let soName = '';
          let arch = '';
          let uuid = '';
          if (tmp.length >= 4) {
            soName = tmp[0];
            arch = tmp[1];
            uuid = tmp[3];
          }
          return [uuid, { soName, arch, uuid }];
        });
      const uuidToSoInfo = Object.fromEntries(uuidSoInfoEntries);

      soInfos = uuids.map((uuid) => {
        const isUsymSymbol = uuid.startsWith('USym');
        let soName;
        let arch;
        if (isUsymSymbol) {
          soName = 'libil2cpp.so';
          arch = '';
        } else {
          soName = uuidToSoInfo[uuid]?.soName;
          arch = uuidToSoInfo[uuid]?.arch;
        }

        // 填充匹配的符号表信息
        // 移动端native匹配规则：只用uuid匹配。旧的逻辑还按version+arch+soName匹配，现在已经去掉了
        const matchedUploadedSymbolInfo = uploadedInfos.find(x => x.uuid.toLowerCase() === uuid.toLowerCase());

        const isUploaded = isNotNullish(matchedUploadedSymbolInfo);
        const matchedSymbolFileUuid = isUploaded ? matchedUploadedSymbolInfo.uuid : '';
        const isUuidMatched = isUploaded && matchedSymbolFileUuid.toLowerCase() === uuid.toLowerCase();
        const isSystemSymbol = CrashModuleUtil.getIsSystemModule(soName, lowerSystemSoNameList, lowerAppSoNameList);
        return {
          soName,
          arch,
          uuid,
          isSystemSymbol,
          isUsymSymbol,
          isUploaded,
          isUuidMatched,
          matchedSymbolFileUuid,
          uploadTime: (matchedUploadedSymbolInfo || {}).uploadTime,
          matchedUploadedSymbolInfo,
        };
      });
    } else if (PlatformUtil.isIosOrMac(pid) && !isShowingMinidumpStack) {
      const uuids = CrashUtil.getSymbolUuidList({ pid, crashDoc, attachList, pcModuleInfoList, attachAllKvList, enableUsym });
      const soInfoLines = DataDetailUtil.makeSoInfoTextLines(pid, attachList);

      soInfos = uuids.map(uuid => {
        const isUsymSymbol = uuid.startsWith('USym');
        const line = soInfoLines.find(x => x.includes(uuid.toLowerCase())) || '';
        let soName;
        if (isUsymSymbol) {
          soName = 'UnityFramework';
        } else {
          soName = line.split(':')[0] || '';
        }
        const matchedUploadedSymbolInfo = uploadedInfos.find(x => x.uuid.toLowerCase() === uuid.toLowerCase());
        const isUploaded = isNotNullish(matchedUploadedSymbolInfo);
        const isSystemSymbol = !isUsymSymbol && CrashModuleUtil.getIsSystemModule(soName, lowerSystemSoNameList, lowerAppSoNameList);
        return {
          soName,
          uuid,
          isSystemSymbol,
          isUsymSymbol,
          isUploaded,
          isUuidMatched: isUploaded,
          matchedSymbolFileUuid: isUploaded ? uuid : '',
          uploadTime: (matchedUploadedSymbolInfo || {}).uploadTime,
          matchedUploadedSymbolInfo,
        }
      });
    } else if (Array.isArray(pcModuleInfoList)) {
      soInfos = pcModuleInfoList.filter(x => x.uuid).map(({ moduleName, uuid, minidumpSymbolType, relatedUnwindUuid }) => {
        const lowerUuid = uuid.toLowerCase();
        const matchedUploadedSymbolInfo = uploadedInfos.find(x => x.uuid.toLowerCase() === lowerUuid);
        const isUploaded = isNotNullish(matchedUploadedSymbolInfo);
        const isSystemSymbol = lowerSystemSymbolUuids.includes(lowerUuid)
          || lowerSystemSymbolUuids.includes((relatedUnwindUuid || '').toLowerCase());
        return {
          soName: moduleName,
          uuid,
          minidumpSymbolType,
          isSystemSymbol,
          isUploaded,
          isUuidMatched: isUploaded,
          matchedSymbolFileUuid: isUploaded ? uuid : '',
          uploadTime: (matchedUploadedSymbolInfo || {}).uploadTime,
          matchedUploadedSymbolInfo,
        };
      }).filter(x => { // 系统符号表只显示不加0的那个uuid
        if (x.isSystemSymbol && x.minidumpSymbolType === MinidumpSymbolType.SYMBOLIC_INFO) {
          return false;
        }
        return true;
      });
    }

    const hasAnyInfoNoSymbolMatched = soInfos.some(x => !x.isUploaded && !x.isSystemSymbol);

    // 过滤被忽略的so符号表
    const thisCrashIgnoreInfos = [];
    if (ignoreSymbolInfo.length > 0) {
      for (let i = 0; i < ignoreSymbolInfo.length; ++i) {
        for (let j = 0; j < soInfos.length; ++j) {
          if (ignoreSymbolInfo[i].soName === soInfos[j].soName) {
            thisCrashIgnoreInfos.push(ignoreSymbolInfo[i]);
          }
        }
      }

      soInfos = soInfos.filter((info) => {
        // 如果与ignoreSymbol中的soName一致，则被忽略
        let isIgnore = false;
        for (let i = 0; i < thisCrashIgnoreInfos.length; ++i) {
          if (thisCrashIgnoreInfos[i].soName === info.soName) {
            isIgnore = true;
            break;
          }
        }
        return !isIgnore;
      });
    }

    // 更新tips
    if (shouldSendAction) {
      if (hasAnyInfoNoSymbolMatched) {
        actions.changeSymbolTips('no_all_symbol');
      } else {
        actions.changeSymbolTips('');
      }
    }

    return {
      productVersion,
      soInfos,
      thisCrashIgnoreInfos,
    };
  }

  jumpToSymbolMgrPage() {
    const { actions, currentApp } = this.props;
    const { appId, pid } = currentApp.toJS();
    window.open(`/crash-reporting/preferences/dsyms/${appId}?pid=${pid}`,'_blank');
  }

  loadInitialData() {
    const { issue, fetchSymbolInfoAndIgnoreSymbolInfo } = this.props;
    const { crashDoc } = issue.toJS();
    if (!crashDoc) {
      return;
    }
    fetchSymbolInfoAndIgnoreSymbolInfo();
  }

  async handleIgnore(info) {
    const { actions, isDemoApp } = this.props;
    if (isDemoApp) return;
    await actions.ignoreSymbol(info.soName, info.uuid, info.arch, 1);
    this.loadInitialData();
  }

  async handleUnIgnore(info) {
    const { actions, isDemoApp } = this.props;
    if (isDemoApp) return;
    await actions.ignoreSymbol(info.soName, info.uuid, info.arch, 2);
    this.loadInitialData();
  }

  // 根据crash的类型渲染符号表内容
  renderSymbol(style, symbolInfo, issue, pid, goSymbolTag) {
    const { crashDoc } = issue.toJS();
    if (!crashDoc) {
      return <div></div>;
    }
    const shouldRenderJava = this.getShouldRenderJava();
    const shouldRenderNative = this.getShouldRenderNative();

    const noNeedSymbol = isAndroidOrHarmony(pid) && !shouldRenderNative && !shouldRenderJava;

    return (
      <div>
          <div>
            { crashDoc.crashMap.retraceTime && !noNeedSymbol && <div className={`clearfix ${style.subtitle}`}>
              { <span className="color_gray">{`${this.props.t("SYMBOLTABLE.lastRestoreTime")}${ this.formatWithAppTimeZone(crashDoc.crashMap.retraceTime) }`}</span>}
            </div> }
            {shouldRenderJava && this.renderJavaSymbol(symbolInfo, issue)}
            {shouldRenderNative && this.renderNativeSymbol(symbolInfo, issue, shouldRenderJava)}
            {noNeedSymbol && <div className={_style.no_need_symbol}>{ this.props.t('SYMBOLTABLE.noNeedSymbol')}</div>}
          </div>
      </div>
    );
  }

  // native符号表
  // 展示的native符号表信息，存在crashdoc，为soInfos
  // 已上传的符号表信息，使用soinfos接口获取，存在state的symbol里
  // 对比二者的uuid和架构、soname来判断是否有上传
  renderNativeSymbol(uploadedInfos, issue, shouldRenderJava) {
    const {
      productVersion, soInfos, thisCrashIgnoreInfos,
    } = this.getNativeSymbolInfo(uploadedInfos, issue);
    const { symbolInfo, isDemoApp, appId, pid } = this.props;

    const tableColumns = [{
      // dataIndex: 'soName',
      key: 'soName',
      title: ze('模块名', 'Module'),
      render: (rowData, record, index) => {
        const {
          uploadTime,
          isUploaded,
          isUuidMatched,
          isSystemSymbol,
          isUsymSymbol,
          minidumpSymbolType,
        } = record;
        if (isSystemSymbol) {
          return <Tooltip
            title={ze('系统模块', 'System Module')}
          ><div><span className={`${_style.system_symbol_text}`}>{rowData.soName || ze('系统模块', 'System Module')}</span></div></Tooltip>;
        } else {
          return <div>
            { minidumpSymbolType && <Tooltip
              title={ MinidumpSymbolTypeUtil.makeDesc(minidumpSymbolType) }
            ><Tag
              style={{ userSelect: 'none' }}
              color='geekblue'
            >{ MinidumpSymbolTypeUtil.makeLabel(minidumpSymbolType) }</Tag></Tooltip> }
            <span>{ rowData.soName || '-' }</span>
            { isUsymSymbol && <Tooltip
              title={ <div>
                <span>{ze(
                  'Usym还原：使用Unity生成的.usym符号文件，可以还原得到该地址对应的C#代码行号。详情可参考',
                  'Using Unity to generate .usym symbol files, which can resymbolicate the corresponding C# code line number of the address. For more details, please refer to',
                )}</span>
                <a
                  href={DocsUtil.makeDocsUrl('/sdkDocuments/unity-sdk-new/#configuring-usym-symbolication')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {ze('配置Usym还原', ' Configure Usym Symbolication')}
                </a>
              </div> }
            ><Tag
              style={{ userSelect: 'none', marginLeft: '4px' }}
              color='geekblue'
            >Usym</Tag></Tooltip> }
          </div>;
        }
      }
      }, {
        dataIndex: 'uuid',
        title: <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ paddingRight: 4 }}>UUID</span>
        <Tooltip
          title={(<div>
          {ze(
            'UUID是符号表的内置属性，是符号表的唯一标识，对于同一份文件，该值不会改变。',
            'UUID is a built-in attribute of the symbol table, serving as a unique identifier for the symbol table. For the same file, this value will not change.',
          )}
          <a
            href={DocsUtil.makeDocsUrl('/symbolsDocuments/symbol-table/#CommonQuestions')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ze('查看详情', 'View details')}
          </a>
        </div>)}
        >
          <WrappedTipsIcon />
        </Tooltip>
        </div>,
      }, {
        key: 'symbolMd5',
        title: ze('符号表MD5', 'Symbol File MD5'),
        render: (_, record) => {
        const { matchedUploadedSymbolInfo } = record;
        return (matchedUploadedSymbolInfo || {}).symbolMd5 || '-';
      },
    }, {
      dataIndex: 'arch',
      title: 'Arch',
      disabled: !isAndroidOrHarmony(pid),
    }, {
      key: 'uploadStatus',
      title: <div>
        <span>{ze('上传状态', 'Upload Status')}</span>
        {!isMobile(pid) && <span> ({this.renderSymbolUploadTips()})</span>}
      </div>,
      onCell: () => {
        return {
          style: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        };
      },
      render: (text, record, index) => {
        const { uploadTime, isUploaded, isUuidMatched, isSystemSymbol, matchedUploadedSymbolInfo } = record;
        const { lackDebugInfo } = matchedUploadedSymbolInfo || {};
        if (isSystemSymbol) {
          return <div>{ ze('系统符号表', 'System File (Auto-configured by CrashSight)') }</div>;
        } else if (isUploaded) {
          if (lackDebugInfo) {
            return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ExclamationCircleOutlined style={{ color: orange.primary }} />
              <div>{ ze('缺少调试信息', 'Missing Debug Info') }</div>
              <div>({ this.formatWithAppTimeZone(uploadTime) })</div>
            </div>;
          } else {
            return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <IconRight/>
              <div>{ ze('已上传', 'Uploaded') }</div>
              <div>({ this.formatWithAppTimeZone(uploadTime) })</div>
            </div>;
          }
        } else {
          return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconWrong/>
            <div style={{ color: red.primary }}>{ ze('未上传', 'Not Uploaded') }</div>
            { /* <Popconfirm
              title={ze('要忽略此符号表吗？忽略后将不会提示该符号表的上传状态。', 'Do you want to ignore this symbol file? Ignoring this file will not hint the upload status of it.')}
              onConfirm={() => this.handleIgnore(record)}
            >
              <Button
                type='text'
                style={{ color: '#999', padding: '0' }}
              >{ ze('(忽略)', '(Ignore)') }</Button>
            </Popconfirm> */ }
          </div>
        }
      },
    }, {
      title: ze('操作','Operation'),
      render: (_,record) => {
        const { isUploaded, matchedUploadedSymbolInfo } = record;
        const symbolPath = (matchedUploadedSymbolInfo || {}).symbolPath || '';
        const fileCosKey = String(symbolPath || '').split('/').pop();
        return isUploaded && <div>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              CosUtil.downloadFileViaPresignedUrl(appId, undefined, fileCosKey, fileCosKey);
         }}><CsDownloadButton
            disabled={!isUploaded}
          /></a>
        </div>
      }
    }].filter(x => !x.disabled);

    return (
      <div key="native_symbol">
        <Space size={16} style={{ fontSize: '14px' }}>
          {isMobile(pid) && !isDemoApp && <Button
            style={{ borderColor: 'var(--cs-primary-color-alt)', color: 'var(--cs-primary-color-alt)' }}
            onClick={() => this.jumpToSymbolMgrPage()}
          ><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconManage/>
            <div>{ ze('管理符号表文件', 'Manage Symbol Files')}</div>
          </div></Button> }
          {!shouldRenderJava && this.renderSymbolTips()}
        </Space>
        <div style={{ marginTop: '12px' }}>
          <Table
            size='small'
            columns={tableColumns}
            dataSource={soInfos}
            pagination={false}
          />
        </div>
        {thisCrashIgnoreInfos.length > 0 && (
          <div>
            <div className={`clearfix ${_style.symbol_title}`}>
              <div className={`fl ${_style.symbol_txt}`}>{ this.props.t("SYMBOLTABLE.symbolIgnored") }</div>
              <div className={`fl ${_style.read_rule}`}>
              { this.props.t("SYMBOLTABLE.ignoreRule") }
                <div className={_style.symbol_pop}>
                { this.props.t("SYMBOLTABLE.ignoreRuleTip") }
                </div>
              </div>

            </div>
            <ul className={_style.mod_symbol}>
              {
                thisCrashIgnoreInfos.map((ignoreInfo) => {
                  return (
                    <li>
                      <p className="fl color_gray">
                        <span>{`${ignoreInfo.soName}(${ignoreInfo.uuid} Arch:${ignoreInfo.arch})`}</span>
                      </p>
                      <div className={`fr ${_style.symbol_right}`}>
                        <span
                          className={`color_gray ${_style.cursor_pointer}`}
                          onClick={() => this.handleUnIgnore(ignoreInfo)}>
                          { this.props.t("SYMBOLTABLE.cancelIgnore") }
                        </span>
                      </div>
                    </li>
                  );
                })
              }
            </ul>
          </div>
        )}
      </div>
    );
  }

  // java符号表
  // java符号表一共有两条，一条是本条crash的channel，一条是all；如果infos中有channel等于本crashchannel的，则本crash的channel已上传；如infos中有channel为-1的，则all已上传
  renderJavaSymbol(infos, issue) {
    const { t, isDemoApp, appId, pid } = this.props;
    const {
      uploadingId, uploadPercent, channel, version, isThisCrashChannelUpload, thisCrashChannelInfo,
      isAllChannelUpload, allChannelInfo,
    } = this.getJavaSymbolInfo(infos, issue);

    // 构造表格数据
    const dataSource = [];
    // channel字段没用，现在不展示
    /* if (channel) {
      dataSource.push({
        key: 'channel',
        version: `Version: ${version} Channel: ${channel}`,
        fileName: isThisCrashChannelUpload ? thisCrashChannelInfo.fileName : '',
        isUploaded: isThisCrashChannelUpload,
        uploadTime: isThisCrashChannelUpload ? thisCrashChannelInfo.uploadTime : '',
        filePath: isThisCrashChannelUpload ? thisCrashChannelInfo.filePath : '',
      });
    } */
    dataSource.push({
      key: 'all',
      version: version,
      fileName: isAllChannelUpload ? allChannelInfo.fileName : '',
      isUploaded: isAllChannelUpload,
      uploadTime: isAllChannelUpload ? allChannelInfo.uploadTime : '',
      filePath: isAllChannelUpload ? allChannelInfo.filePath : '',
    });

    const tableColumns = [{
      key: 'version',
      title: t("SYMBOLMANAGE.version"),
      dataIndex: 'version',
    }, {
      key: 'fileName',
      title: t("SYMBOLMANAGE.symFile"),
      dataIndex: 'fileName',
      render: (fileName) => fileName || '-',
    }, {
      key: 'uploadStatus',
      title: ze('上传状态', 'Upload Status'),
      onCell: () => {
        return {
          style: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        };
      },
      render: (_, record) => {
        const { isUploaded, uploadTime } = record;
        if (isUploaded) {
          return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconRight/>
            <div>{ ze('已上传', 'Uploaded') }</div>
            <div>({ this.formatWithAppTimeZone(uploadTime) })</div>
          </div>;
        } else {
          return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconWrong/>
            <div style={{ color: red.primary }}>{ ze('未上传', 'Not Uploaded') }</div>
          </div>
        }
      },
    }, {
      key: 'operation',
      title: ze('操作','Operation'),
      render: (_, record) => {
        const { isUploaded, filePath } = record;
        const fileCosKey = String(filePath || '').split('/').pop();
        return isUploaded && <div>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              CosUtil.downloadFileViaPresignedUrl(appId, undefined, fileCosKey, fileCosKey);
            }}
          ><CsDownloadButton
            disabled={!isUploaded}
          /></a>
        </div>
      }
    }];

    return (
      <div key="java_symbol">
        <Space size={16} style={{ fontSize: '14px' }}>
          {!isDemoApp && <Button
            style={{ borderColor: 'var(--cs-primary-color-alt)', color: 'var(--cs-primary-color-alt)' }}
            onClick={() => this.jumpToSymbolMgrPage()}
          ><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconManage/>
            <div>{ ze('管理符号表文件', 'Manage Symbol Files')}</div>
          </div></Button> }
        </Space>
        <div style={{ marginTop: '12px' }}>
          <Table
            size='small'
            columns={tableColumns}
            dataSource={dataSource}
            pagination={false}
          />
        </div>
      </div>
    );
  }

  renderSymbolTips() {
    const { symbolTips } = this.props;
    if (symbolTips) {
      return (
        <div
          className={`color_${SYMBOL_TIPS[symbolTips].color} ${_style.symbol_tips}`}>
          {
            symbolTips === '40412'
            ?(
              <div>
                { this.props.t("SYMBOLTABLE.40412Tip1") }
                <a href={IOS_SYMBOL_TOOL} className="color_blue">{ this.props.t("SYMBOLTABLE.40412Tip2") }</a>
                { this.props.t("SYMBOLTABLE.40412Tip3") }
              </div>
            ):(
              symbolTips === '40413'
              ? (
                <div>
                  { this.props.t("SYMBOLTABLE.40413Tip1") }
                  <a href={ANDROID_SYMBOL_TOOL} className="color_blue">{ this.props.t("SYMBOLTABLE.40413Tip2") }</a>
                  { this.props.t("SYMBOLTABLE.40413Tip3") }
                </div>
              ): (
                this.props.t(SYMBOL_TIPS[symbolTips].tips)
              )
            )
          }
        </div>
      );
    }
    return null;
  }

  /** 渲染符号表上传的说明文档提示 */
  renderSymbolUploadTips() {
    const { currentApp } = this.props;
    const { teamType } = currentApp.toJS();
    let url = ''
    if (teamType === BillingTeamType.TEST || teamType === BillingTeamType.TENCENT) {
      url = DocsUtil.makeDocsUrl('symbolsDocuments/landun-plugin')
    } else {
      url = DocsUtil.makeDocsUrl('symbolsDocuments/symbol-table')
    }

    return <a href={url} target="_blank">{
        ze('上传教程', 'Upload Tutorial')
    }</a>
  }

  render() {
    const {
      style, issue, pid, symbolInfo, goSymbolTag,
    } = this.props;

    if (!symbolInfo) {
      return <div></div>;
    }

    return this.renderSymbol(style, symbolInfo, issue, pid, goSymbolTag);
  }
}

SymbolTable.propTypes = {
  style: PropTypes.object.isRequired,
  actions: PropTypes.object,
  reduxState: PropTypes.object,
  symbolInfo: PropTypes.array,
  issue: PropTypes.object,
  appId: PropTypes.string,
  pid: PropTypes.string,
  symbolTips: PropTypes.string,
  uploadPercent: PropTypes.number,
  uploadingId: PropTypes.string,
  currentApp: PropTypes.object,
  ignoreSymbolInfo: PropTypes.array,
  isDemoApp: PropTypes.bool,
  goSymbolTag: PropTypes.func,
  currentUser: PropTypes.object,
  fetchSymbolInfoAndIgnoreSymbolInfo: PropTypes.func,
};

// 设置props
export default connect((state) => {
  return {
    reduxState: state,
    currentApp: state.app.get('current'),
    currentUser: state.user.get('current'),
    uploadingId: state.symbol.get('uploadingId'),
    uploadPercent: state.symbol.get('uploadPercent'),
    symbolTips: state.symbol.get('symbolTips'),
    isDemoApp: state.app.get('isDemoApp'),
    dsymsSize: state.global.get('dsymsSize'),
  };
}, (dispatch) => ({
  actions: bindActionCreators({
    ...symbolActions,
    pushState,
    replaceState,
  }, dispatch),
  dispatch,
}))(SymbolTable);
